import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, BookOpen, Heart, Sparkles, MessageCircle, Sunrise } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GATHERING_IMG = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/e10d0153-5fbb-484e-b9da-2908a1ba4b5c/wmwe+pic+me+and+jania.jpg";

const includes = [
    { icon: Heart, label: "Monthly fellowship gatherings" },
    { icon: Sparkles, label: "Connection Circle placement" },
    { icon: MessageCircle, label: "Access to the private community hub" },
    { icon: BookOpen, label: "Digital reflections + guided prompts" },
    { icon: Sunrise, label: "Early access to gatherings" },
    { icon: Heart, label: "Member pricing for retreats" },
    { icon: Sparkles, label: "Priority access to the Fellowship Cohort" },
    { icon: BookOpen, label: "Exclusive content from Shana" },
];

const rhythm = [
    "A themed fellowship gathering",
    "A guided reflection",
    "A Connection Circle touchpoint",
    "A community hub check-in",
    "A moment of prayer + encouragement",
];

export default function WMWEFellowship() {
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", tier_interest: "undecided" });
    const [loading, setLoading] = useState(false);

    const submitWaitlist = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/membership/waitlist`, form);
            toast.success("You're on the list. We'll be in touch as membership opens.");
            setForm({ first_name: "", last_name: "", email: "", tier_interest: "undecided" });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    const inputClass =
        "w-full font-sans text-base bg-sanct-cream/60 border border-sanct-clay/60 rounded-xl px-5 py-3.5 outline-none focus:border-sanct-rose transition-colors text-sanct-cocoa placeholder:text-sanct-cocoa/40";

    return (
        <main data-testid="wmwe-fellowship" className="bg-sanct-sand text-sanct-charcoal">
            {/* SECTION 1 — What It Means to Be a Member */}
            <section className="py-28 md:py-36 bg-sanct-cream text-center">
                <div className="max-w-[820px] mx-auto px-6 md:px-10">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-6">The Fellowship</p>
                    <h1 className="font-serif text-5xl md:text-7xl text-sanct-cocoa leading-[1] tracking-tight">
                        Membership is not access.
                        <span className="block italic text-sanct-rose mt-2">It&apos;s belonging.</span>
                    </h1>
                    <p className="font-serif italic text-xl md:text-2xl text-sanct-cocoa/80 mt-10 leading-[1.6]">
                        It&apos;s being part of a sisterhood that grows together, prays together, learns together, and walks through life together.
                    </p>
                    <p className="font-sans text-base text-sanct-charcoal/75 mt-6 leading-[1.8]">
                        This is a home you can return to &mdash; month after month, season after season.
                    </p>
                </div>
            </section>

            {/* SECTION 2 — What's Included (two-column list, rounded cards with soft icons) */}
            <section className="py-24 md:py-32 bg-sanct-sand">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10">
                    <div className="text-center max-w-[640px] mx-auto mb-14">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">What&apos;s Included</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-sanct-cocoa leading-[1.1] tracking-tight">
                            Every member receives.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {includes.map((item) => {
                            const Icon = item.icon;
                            return (
                                <div key={item.label} className="flex items-center gap-5 bg-sanct-cream rounded-[20px] p-6 border border-sanct-clay/40">
                                    <div className="w-12 h-12 rounded-full bg-sanct-clay/60 flex items-center justify-center flex-shrink-0">
                                        <Icon size={20} className="text-sanct-cocoa" strokeWidth={1.5} />
                                    </div>
                                    <p className="font-serif text-lg md:text-xl text-sanct-cocoa leading-tight">{item.label}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 3 — Monthly Rhythm (timeline) */}
            <section className="py-24 md:py-32 bg-sanct-clay/30">
                <div className="max-w-[1000px] mx-auto px-6 md:px-10">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">The Monthly Rhythm</p>
                    <h2 className="font-serif text-4xl md:text-5xl text-sanct-cocoa leading-[1.1] tracking-tight mb-14">
                        Every month, members receive:
                    </h2>
                    <ol className="relative border-l-2 border-sanct-rose/40 pl-10 md:pl-14 space-y-10">
                        {rhythm.map((line, i) => (
                            <li key={line} className="relative">
                                <span className="absolute -left-[52px] md:-left-[62px] top-1 w-8 h-8 rounded-full bg-sanct-rose text-sanct-cream font-serif italic flex items-center justify-center text-sm">{i + 1}</span>
                                <p className="font-serif italic text-xl md:text-2xl text-sanct-cocoa leading-[1.3]">{line}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* SECTION 4 — Connection Circles (image + text, soft clay) */}
            <section className="py-24 md:py-32 bg-sanct-sand">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-5">
                        <img src={GATHERING_IMG} alt="Connection Circle" className="w-full aspect-[4/5] object-cover rounded-sm" />
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">Connection Circles</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-sanct-cocoa leading-[1.05] tracking-tight">
                            Your circle is your
                            <span className="block italic text-sanct-rose mt-1">spiritual support system.</span>
                        </h2>
                        <p className="font-sans text-base md:text-lg text-sanct-charcoal/85 mt-8 leading-[1.8]">
                            A small group of women who walk with you, pray with you, and grow with you. This is where sisterhood becomes family.
                        </p>
                        <p className="font-serif italic text-2xl md:text-3xl text-sanct-rose mt-10 leading-[1.3]">
                            The WMWE Promise: you will never walk alone again.
                        </p>
                    </div>
                </div>
            </section>

            {/* SECTION 5 — Pricing (3 cards, founding member featured in muted rose) */}
            <section className="py-24 md:py-32 bg-sanct-cream" id="tiers" data-testid="membership-tiers-section">
                <div className="max-w-[1300px] mx-auto px-6 md:px-10">
                    <div className="text-center mb-16">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">Membership</p>
                        <h2 className="font-serif text-4xl md:text-6xl text-sanct-cocoa leading-[1.05] tracking-tight">
                            Choose your rhythm.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Monthly */}
                        <div className="bg-sanct-sand border border-sanct-clay/50 rounded-[24px] p-10 flex flex-col">
                            <p className="font-sans text-xs uppercase tracking-[0.25em] text-sanct-cocoa/60 mb-5">Monthly</p>
                            <div className="flex items-baseline gap-2 mb-4">
                                <span className="font-serif text-6xl text-sanct-cocoa">$47</span>
                                <span className="font-sans text-sm text-sanct-charcoal/60">/month</span>
                            </div>
                            <p className="font-sans text-sm text-sanct-charcoal/75 mb-8 leading-[1.7]">
                                Flexible month&#8209;to&#8209;month belonging. Cancel anytime.
                            </p>
                            <ul className="space-y-3 font-sans text-sm text-sanct-charcoal/80 mb-8">
                                <li className="flex gap-2"><Check size={16} className="text-sanct-rose mt-0.5" /> Full fellowship access</li>
                                <li className="flex gap-2"><Check size={16} className="text-sanct-rose mt-0.5" /> Connection Circle placement</li>
                                <li className="flex gap-2"><Check size={16} className="text-sanct-rose mt-0.5" /> Member pricing on retreats</li>
                            </ul>
                        </div>
                        {/* Annual */}
                        <div className="bg-sanct-sand border border-sanct-clay/50 rounded-[24px] p-10 flex flex-col">
                            <p className="font-sans text-xs uppercase tracking-[0.25em] text-sanct-cocoa/60 mb-5">Annual</p>
                            <div className="flex items-baseline gap-2 mb-4">
                                <span className="font-serif text-6xl text-sanct-cocoa">$470</span>
                                <span className="font-sans text-sm text-sanct-charcoal/60">/year</span>
                            </div>
                            <p className="font-sans text-sm text-sanct-charcoal/75 mb-8 leading-[1.7]">
                                Anchor your full year in fellowship. Save $94 vs monthly.
                            </p>
                            <ul className="space-y-3 font-sans text-sm text-sanct-charcoal/80 mb-8">
                                <li className="flex gap-2"><Check size={16} className="text-sanct-rose mt-0.5" /> Everything in Monthly</li>
                                <li className="flex gap-2"><Check size={16} className="text-sanct-rose mt-0.5" /> Priority Fellowship Cohort access</li>
                                <li className="flex gap-2"><Check size={16} className="text-sanct-rose mt-0.5" /> Exclusive annual retreat invitation</li>
                            </ul>
                        </div>
                        {/* Founding Member — featured */}
                        <div className="bg-sanct-rose text-sanct-cream rounded-[24px] p-10 flex flex-col relative shadow-xl" data-testid="founding-member-card">
                            <span className="absolute -top-3 left-10 bg-sanct-cocoa text-sanct-cream text-[10px] uppercase tracking-[0.25em] px-4 py-1.5 rounded-full">Founding Member</span>
                            <p className="font-sans text-xs uppercase tracking-[0.25em] text-sanct-cream/80 mb-5 mt-2">Limited — First Cohort</p>
                            <div className="flex items-baseline gap-2 mb-1">
                                <span className="font-serif text-6xl">$27</span>
                                <span className="font-sans text-sm text-sanct-cream/80">/month</span>
                            </div>
                            <p className="font-sans text-xs italic text-sanct-cream/80 mb-4">or $270/year &mdash; locked for life</p>
                            <p className="font-sans text-sm text-sanct-cream/90 mb-8 leading-[1.7]">
                                Be among the first to help shape the fellowship. Founding pricing is honored as long as your membership stays active.
                            </p>
                            <ul className="space-y-3 font-sans text-sm text-sanct-cream/90 mb-8">
                                <li className="flex gap-2"><Check size={16} className="text-sanct-cream mt-0.5" /> Everything in Annual</li>
                                <li className="flex gap-2"><Check size={16} className="text-sanct-cream mt-0.5" /> Founding circle — direct access to Shana</li>
                                <li className="flex gap-2"><Check size={16} className="text-sanct-cream mt-0.5" /> Lifetime-locked founding pricing</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 6 — CTA + Waitlist */}
            <section className="py-24 md:py-32 bg-sanct-sand" id="waitlist" data-testid="waitlist-section">
                <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
                    <div className="lg:col-span-5 text-center lg:text-left">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">The Waitlist</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-sanct-cocoa leading-[1.05] tracking-tight">
                            Your seat is <span className="italic text-sanct-rose">waiting.</span>
                        </h2>
                        <p className="font-sans text-base md:text-lg text-sanct-charcoal/80 mt-7 leading-[1.7]">
                            Membership opens in waves. Join the waitlist and you&apos;ll be among the first invited when doors open — including founding-member pricing.
                        </p>
                    </div>

                    <form onSubmit={submitWaitlist} className="lg:col-span-7 bg-sanct-cream rounded-[24px] p-8 md:p-10 space-y-6 border border-sanct-clay/40" data-testid="waitlist-form">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="font-sans text-xs uppercase tracking-[0.25em] text-sanct-cocoa/70 block mb-2">First Name *</label>
                                <input type="text" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputClass} data-testid="waitlist-firstname-input" />
                            </div>
                            <div>
                                <label className="font-sans text-xs uppercase tracking-[0.25em] text-sanct-cocoa/70 block mb-2">Last Name *</label>
                                <input type="text" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputClass} data-testid="waitlist-lastname-input" />
                            </div>
                        </div>
                        <div>
                            <label className="font-sans text-xs uppercase tracking-[0.25em] text-sanct-cocoa/70 block mb-2">Email *</label>
                            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} data-testid="waitlist-email-input" />
                        </div>
                        <div>
                            <label className="font-sans text-xs uppercase tracking-[0.25em] text-sanct-cocoa/70 block mb-3">Preferred Tier</label>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { v: "monthly", l: "Monthly — $47" },
                                    { v: "annual", l: "Annual — $470" },
                                    { v: "founding_monthly", l: "Founding $27/mo" },
                                    { v: "founding_annual", l: "Founding $270/yr" },
                                    { v: "undecided", l: "Not sure yet" },
                                ].map((t) => (
                                    <button
                                        type="button"
                                        key={t.v}
                                        onClick={() => setForm({ ...form, tier_interest: t.v })}
                                        data-testid={`waitlist-tier-${t.v}`}
                                        className={`px-4 py-2.5 text-xs uppercase tracking-[0.16em] rounded-full transition-colors ${
                                            form.tier_interest === t.v
                                                ? "bg-sanct-rose text-sanct-cream"
                                                : "bg-sanct-sand text-sanct-cocoa border border-sanct-clay/60 hover:border-sanct-rose"
                                        }`}
                                    >
                                        {t.l}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="waitlist-submit-btn"
                            className="group inline-flex items-center gap-3 px-8 py-4 bg-sanct-cocoa text-sanct-cream text-xs uppercase tracking-[0.25em] rounded-full hover:bg-sanct-rose transition-colors duration-300 disabled:opacity-50"
                        >
                            {loading ? "Joining..." : "Join the Waitlist"}
                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </form>
                </div>
            </section>
        </main>
    );
}
